/**
 * Subscription Step Manager
 * 
 * Manages which subscription purchase step component to render based on current step.
 * Similar to BookingStepManager pattern.
 * 
 * Steps:
 * - 0: SubscriptionSelectionStep - Choose membership or pass
 * - 1: SubscriptionReviewStep - Email + payment method (auto-advances)
 * - 2: FinishAccountStep - Account creation (pending users) + subscription purchase
 * 
 * @module subscription/components
 */

import type { ReactElement } from 'react';
import { SUBSCRIPTION_STEPS, type SubscriptionStepId } from '~/services/internalApi/subscription/context';

/**
 * Props for SubscriptionStepManager
 */
export interface SubscriptionStepManagerProps {
  /** Current active step */
  currentStep: SubscriptionStepId;
  /** Step components (must be exactly 3: Selection, Review, Finish Account) */
  children: [ReactElement, ReactElement, ReactElement];
}

/**
 * Subscription Step Manager
 * 
 * Renders the appropriate step component based on currentStep.
 * 
 * @example
 * ```tsx
 * <SubscriptionStepManager currentStep={currentStep}>
 *   <SubscriptionSelectionStep />
 *   <SubscriptionReviewStep />
 *   <FinishAccountStep />
 * </SubscriptionStepManager>
 * ```
 */
export function SubscriptionStepManager({ 
  currentStep, 
  children 
}: SubscriptionStepManagerProps) {
  // Validate we have exactly 3 step components
  if (!Array.isArray(children) || children.length !== 3) {
    throw new Error('SubscriptionStepManager requires exactly 3 children (steps)');
  }
  
  // Render the appropriate step component
  switch (currentStep) {
    case SUBSCRIPTION_STEPS.PLAN_SELECTION:
      return children[0];
    
    case SUBSCRIPTION_STEPS.REVIEW:
      return children[1];
    
    case SUBSCRIPTION_STEPS.COMPLETE_ACCOUNT:
      return children[2];
    
    default:
      console.error('[SubscriptionStepManager] Unknown step:', currentStep);
      return children[0]; // Fallback to first step
  }
}
